import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Users, Filter } from "lucide-react";
import { SIGNATURE_PROGRAMS, PROGRAM_BY_SLUG, STATUS_LABELS, STATUS_STYLES } from "../lib/signaturePrograms";
import { getProgramStatus } from "../lib/api";

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES["operating"];
  const label = STATUS_LABELS[status] || "Operating";
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${style}`} data-testid={`status-${status}`}>
      {label}
    </span>
  );
}

function ProgramsIndex() {
  const [filter, setFilter] = useState("All");
  const { data: statusMap = {} } = useQuery({ queryKey: ["program-status"], queryFn: getProgramStatus });

  const filtered = SIGNATURE_PROGRAMS.filter((p) => {
    if (filter === "All") return true;
    if (filter === "Signature") return p.signature;
    const s = statusMap[p.slug] || "operating";
    return s === filter.toLowerCase().replace(/\s+/g, "-");
  });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="programs-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Our Programs</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02]">
              Ten programs. <span className="italic text-[#F5B90A]">One mission.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              Every young man is different. Every family is different. Our programs are built so your son can grow in the areas
              where he needs it most — anchored by our signature Change Choices: Strong Minds curriculum.
            </p>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="bg-[#F7F3EC] py-8 border-y border-black/5" data-testid="programs-filters">
        <div className="container-x flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-black/60"><Filter className="h-4 w-4" /> Filter:</span>
          {["All", "Signature", "Enrolling Now", "Currently Operating", "Pilot Program", "Coming Soon"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`program-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${filter === f ? "bg-[#6B21A8] text-white" : "bg-white text-black/80 border border-black/10"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="bg-white py-16 md:py-20" data-testid="programs-grid">
        <div className="container-x">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => {
              const s = statusMap[p.slug] || "operating";
              return (
                <motion.div key={p.slug}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}>
                  <Link to={`/programs/${p.slug}`} className="card-editorial p-7 block group h-full" data-testid={`program-tile-${p.slug}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#6B21A8]/10 text-[#6B21A8]">
                        <p.icon className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-display text-xs font-bold text-black/40">{p.letter}</span>
                        <StatusBadge status={s} />
                      </div>
                    </div>
                    {p.signature && <p className="mt-5 overline text-[#F5B90A]">★ Signature Program</p>}
                    <h3 className={`font-display text-xl font-bold group-hover:text-[#6B21A8] ${p.signature ? "mt-2" : "mt-5"}`}>
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-black/70">{p.summary}</p>
                    <div className="mt-5 flex items-center gap-3 text-xs text-black/50">
                      <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> Ages {p.ages}</span>
                      {p.duration && <span>· {p.duration}</span>}
                    </div>
                    <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#6B21A8]">
                      Learn more <ArrowRight className="h-4 w-4" />
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-black/60 py-14">No programs match this filter.</p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white py-20" data-testid="programs-cta">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl font-bold">Not sure where to start?</h2>
          <p className="mt-4 text-white/70 text-lg">We'll help you choose the right program for your son.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/enroll" className="btn-gold">Start an Inquiry <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="btn-outline-white">Talk to Our Team</Link>
          </div>
        </div>
      </section>
    </>
  );
}

function ProgramDetail({ slug }) {
  const p = PROGRAM_BY_SLUG[slug];
  const { data: statusMap = {} } = useQuery({ queryKey: ["program-status"], queryFn: getProgramStatus });

  if (!p) {
    return (
      <section className="container-x py-24 max-w-xl">
        <h1 className="font-display text-3xl font-bold">Program not found.</h1>
        <Link to="/programs" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6B21A8]"><ArrowLeft className="h-4 w-4" /> All programs</Link>
      </section>
    );
  }
  const s = statusMap[p.slug] || "operating";

  return (
    <>
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid={`program-detail-${p.slug}`}>
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-20 md:py-28 grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-8">
            <Link to="/programs" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F5B90A]"><ArrowLeft className="h-4 w-4" /> All programs</Link>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <span className="overline text-[#F5B90A]">Program {p.letter}</span>
              <StatusBadge status={s} />
              {p.signature && <span className="overline text-[#F5B90A]">★ Signature</span>}
            </div>
            <h1 className="mt-3 font-display text-5xl md:text-6xl font-bold leading-[1.02]">{p.title}</h1>
            {p.tagline && <p className="mt-5 text-lg text-[#F5B90A] italic">"{p.tagline}"</p>}
            <p className="mt-6 max-w-2xl text-lg text-white/80">{p.summary}</p>
            <div className="mt-8 flex items-center gap-5 text-sm text-white/70">
              <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> Ages {p.ages}</span>
              {p.duration && <span>· {p.duration}</span>}
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              {(p.ctas || [
                { label: "Enroll Your Child", to: "/enroll" },
                { label: "Partner With Us", to: "/partnerships" },
                { label: "Donate", to: "/donate" },
              ]).map((cta, i) => (
                <Link key={i} to={cta.to} className={i === 0 ? "btn-gold" : "btn-outline-white"} data-testid={`program-cta-${i}`}>
                  {cta.label}
                </Link>
              ))}
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="rounded-2xl bg-white/5 border border-white/15 p-10 text-center">
              <p.icon className="h-16 w-16 mx-auto text-[#F5B90A]" strokeWidth={1.6} />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="program-curriculum">
        <div className="container-x grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="overline text-[#6B21A8]">What Participants Experience</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">The approach.</h2>
            <p className="mt-5 text-black/70 text-lg">{p.experience}</p>
          </div>
          <div className="md:col-span-7">
            {p.curriculum.map((c) => (
              <div key={c.heading} className="mb-8">
                <h3 className="font-display text-2xl font-bold">{c.heading}</h3>
                <ul className="mt-4 grid gap-2 md:grid-cols-2">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-black/80">
                      <Check className="h-4 w-4 mt-0.5 text-[#6B21A8] flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {p.disclaimer && (
        <section className="bg-[#F7F3EC] py-14" data-testid="program-disclaimer">
          <div className="container-x mx-auto max-w-3xl rounded-lg bg-white border border-black/10 p-7 text-center">
            <p className="overline text-[#6B21A8]">Important</p>
            <p className="mt-3 text-black/80">{p.disclaimer}</p>
          </div>
        </section>
      )}
    </>
  );
}

export default function Programs() {
  const { slug } = useParams();
  return slug ? <ProgramDetail slug={slug} /> : <ProgramsIndex />;
}
